import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getDiagnosticStats, DiagnosticStats } from "@/lib/diagnosticService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, TrendingUp, BarChart3, Clock, LogOut } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";

const Stats = () => {
  const [stats, setStats] = useState<DiagnosticStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getDiagnosticStats();
        setStats(data);
      } catch (err) {
        setError("Error al cargar las estadísticas");
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-destructive">{error || "Error desconocido"}</p>
      </div>
    );
  }

  const totalDistribution = stats.scoreDistribution.low + stats.scoreDistribution.medium + stats.scoreDistribution.high;

  const handleLogout = async () => {
    await signOut();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container px-4">
        {/* Header with logout */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-sm text-muted-foreground">
            Conectado como: <span className="font-medium text-foreground">{user?.email}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-2 text-center">
            Estadísticas del <span className="text-gradient-gold">Test de Capital de Marca</span>
          </h1>
          <p className="text-muted-foreground text-center mb-12">
            Análisis de respuestas y métricas del diagnóstico
          </p>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total de Leads
                </CardTitle>
                <Users className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.totalLeads}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Puntaje Promedio
                </CardTitle>
                <TrendingUp className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.averageScore}/100</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Alto Valor (80-100)
                </CardTitle>
                <BarChart3 className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-500">{stats.scoreDistribution.high}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {totalDistribution > 0 ? Math.round((stats.scoreDistribution.high / totalDistribution) * 100) : 0}% del total
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Bajo Valor (0-30)
                </CardTitle>
                <BarChart3 className="h-5 w-5 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-500">{stats.scoreDistribution.low}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {totalDistribution > 0 ? Math.round((stats.scoreDistribution.low / totalDistribution) * 100) : 0}% del total
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Score Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mb-12"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Distribución de Puntajes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Low */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Estructura sin Valor (0-30)</span>
                    <span className="text-sm font-medium">{stats.scoreDistribution.low}</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-500 transition-all duration-500"
                      style={{ width: `${totalDistribution > 0 ? (stats.scoreDistribution.low / totalDistribution) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                {/* Medium */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Activo Desaprovechado (40-70)</span>
                    <span className="text-sm font-medium">{stats.scoreDistribution.medium}</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 transition-all duration-500"
                      style={{ width: `${totalDistribution > 0 ? (stats.scoreDistribution.medium / totalDistribution) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                {/* High */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Activo de Alto Valor (80-100)</span>
                    <span className="text-sm font-medium">{stats.scoreDistribution.high}</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${totalDistribution > 0 ? (stats.scoreDistribution.high / totalDistribution) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Últimos Leads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recentLeads.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Aún no hay leads registrados
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Nombre</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Puntaje</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentLeads.map((lead) => (
                        <tr key={lead.id} className="border-b border-border/50 hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm">{lead.name}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">{lead.email}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center justify-center w-12 h-8 rounded-full text-sm font-medium ${
                              lead.totalScore <= 30 
                                ? "bg-red-500/20 text-red-500" 
                                : lead.totalScore <= 70 
                                  ? "bg-yellow-500/20 text-yellow-500" 
                                  : "bg-green-500/20 text-green-500"
                            }`}>
                              {lead.totalScore}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground text-right">
                            {format(new Date(lead.createdAt), "dd MMM yyyy, HH:mm", { locale: es })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Stats;
